import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import AuthenticationWrapper from '../../components/ui/AuthenticationWrapper';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import SubscriptionTierCard from './components/SubscriptionTierCard';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import TrustSignals from './components/TrustSignals';
import { PLANS, PLAN_ORDER, PLAN_SLUGS } from '../../config/plans';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signUp } = useAuth();

  // ✅ redirect robuste: query > state > fallback
  const redirectTo = useMemo(() => {
    return (
      searchParams.get('redirect') ||
      location.state?.from ||
      '/main-dashboard'
    );
  }, [searchParams, location.state]);

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    selectedTier: PLAN_SLUGS.TRIAL,
    agreeToTerms: false,
    agreeToPrivacy: false,
    subscribeNewsletter: true
  });

  const [errors, setErrors] = useState({});

  const subscriptionTiers = PLAN_ORDER.map((slug) => {
    const plan = PLANS[slug];
    const priceMatch = plan.priceLabel.match(/\d+/);
    return {
      tier: plan.slug,
      name: plan.name,
      price: priceMatch ? Number(priceMatch[0]) : 0,
      period: plan.interval ? '/mois' : '',
      trialDays: 0,
      isPopular: !!plan.recommended,
      features: plan.features.map((text) => ({ text, included: true })),
    };
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) newErrors.firstName = 'Le prénom est requis';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Le nom de famille est requis';

    if (!formData?.email?.trim()) {
      newErrors.email = "L'adresse courriel est requise";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = 'Adresse courriel invalide';
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer votre mot de passe';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData?.agreeToTerms) newErrors.agreeToTerms = "Vous devez accepter les conditions d'utilisation";
    if (!formData?.agreeToPrivacy) newErrors.agreeToPrivacy = 'Vous devez accepter la politique de confidentialité';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleContinueToStep2 = () => {
    if (validateStep1()) setCurrentStep(2);
  };

  const handleBackToStep1 = () => setCurrentStep(1);

  const handleTierSelect = (tier) => {
    setFormData(prev => ({ ...prev, selectedTier: tier }));
  };

  // ✅ Social login Google (utilisable dans Register)
  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    setErrors({});

    try {
      // On sauvegarde ce que l'utilisateur vient de remplir (utile pour upsert profile après callback)
      sessionStorage.setItem('pending_profile', JSON.stringify({
        full_name: `${formData?.firstName || ''} ${formData?.lastName || ''}`.trim(),
        first_name: formData?.firstName || '',
        last_name: formData?.lastName || '',
        subscribe_newsletter: !!formData?.subscribeNewsletter,
        intended_tier: formData?.selectedTier || PLAN_SLUGS.TRIAL
      }));

      const callbackUrl = `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectTo)}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl
        }
      });

      if (error) {
        setErrors({ general: `Erreur lors de la connexion avec ${provider}. Veuillez réessayer.` });
        setIsLoading(false);
      }
      // sinon supabase redirige automatiquement
    } catch (err) {
      setErrors({ general: 'Erreur réseau. Veuillez réessayer.' });
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!validateStep2()) return;

    setIsLoading(true);
    setErrors({});

    const { data, error } = await signUp(
      formData?.email,
      formData?.password,
      {
        full_name: `${formData?.firstName} ${formData?.lastName}`,
        first_name: formData?.firstName,
        last_name: formData?.lastName,
        subscription_tier: formData?.selectedTier || PLAN_SLUGS.TRIAL
      }
    );

    if (error) {
      setIsLoading(false);
      let errorMessage = "Une erreur est survenue lors de l'inscription. Veuillez réessayer.";

      if (error?.message?.includes('already registered')) {
        errorMessage = 'Cette adresse courriel est déjà utilisée.';
      } else if (error?.message?.includes('Password')) {
        errorMessage = 'Le mot de passe doit contenir au moins 8 caractères.';
      } else if (error?.message) {
        errorMessage = error?.message;
      }

      setErrors({ general: errorMessage });
      return;
    }

    // ✅ Option B: pas de confirmation email => redirection immédiate
    setIsLoading(false);

    // Si tu utilises le intended_tier pour déclencher un checkout / paywall après login
    if (formData?.selectedTier !== PLAN_SLUGS.TRIAL) {
      sessionStorage.setItem('intended_tier', formData?.selectedTier);
    }

    navigate(redirectTo);
  };

  return (
    <AuthenticationWrapper
      title="Créer un compte"
      subtitle="Commencez à analyser vos investissements immobiliers au Québec"
      showLogo={true}
    >
      <div style={{ paddingTop: 'var(--spacing-md)' }}>

        {/* ✅ Message d’erreur général */}
        {errors?.general && (
          <div style={{
            padding: 'var(--spacing-sm)',
            background: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid var(--color-error)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-md)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-xs)'
          }}>
            <Icon name="AlertCircle" size={20} color="var(--color-error)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--color-error)',
              margin: 0,
              lineHeight: 1.5
            }}>
              {errors?.general}
            </p>
          </div>
        )}

        {/* ✅ Stepper */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--spacing-md)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: currentStep >= 1 ? 'var(--color-primary)' : 'var(--color-muted)',
              color: currentStep >= 1 ? 'var(--color-primary-foreground)' : 'var(--color-muted-foreground)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-caption)',
              fontSize: '0.875rem',
              fontWeight: 600,
              border: '2px solid',
              borderColor: currentStep >= 1 ? 'var(--color-primary)' : 'var(--color-border)'
            }}>
              {currentStep > 1 ? <Icon name="Check" size={16} /> : '1'}
            </div>
          </div>

          <div style={{
            width: '48px',
            height: '2px',
            background: currentStep >= 2 ? 'var(--color-primary)' : 'var(--color-border)',
            alignSelf: 'center'
          }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: currentStep >= 2 ? 'var(--color-primary)' : 'var(--color-muted)',
              color: currentStep >= 2 ? 'var(--color-primary-foreground)' : 'var(--color-muted-foreground)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-caption)',
              fontSize: '0.875rem',
              fontWeight: 600,
              border: '2px solid',
              borderColor: currentStep >= 2 ? 'var(--color-primary)' : 'var(--color-border)'
            }}>
              2
            </div>
          </div>
        </div>

        {/* ✅ STEP 1 */}
        {currentStep === 1 && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{
              background: 'var(--color-card)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-lg)',
              padding: 'var(--spacing-lg) var(--spacing-md)',
              border: '1px solid var(--color-border)'
            }} className="md:p-8">

              {/* ✅ Google button */}
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => handleSocialLogin('google')}
                  disabled={isLoading}
                  iconName="Mail"
                  iconPosition="left"
                  fullWidth
                >
                  Continuer avec Google
                </Button>

                <div style={{
                  position: 'relative',
                  margin: 'var(--spacing-md) 0',
                  textAlign: 'center'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: 'var(--color-border)'
                  }} />
                  <span style={{
                    position: 'relative',
                    display: 'inline-block',
                    padding: '0 var(--spacing-sm)',
                    background: 'var(--color-card)',
                    fontFamily: 'var(--font-caption)',
                    fontSize: '0.875rem',
                    color: 'var(--color-muted-foreground)'
                  }}>
                    Ou créer un compte avec votre courriel
                  </span>
                </div>
              </div>

              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-lg)',
                textAlign: 'center'
              }}>
                Informations personnelles
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-md)'
              }} className="md:grid-cols-2">
                <Input
                  label="Prénom"
                  type="text"
                  name="firstName"
                  placeholder="Jean"
                  value={formData?.firstName}
                  onChange={handleInputChange}
                  error={errors?.firstName}
                  required
                  disabled={isLoading}
                />
                <Input
                  label="Nom de famille"
                  type="text"
                  name="lastName"
                  placeholder="Tremblay"
                  value={formData?.lastName}
                  onChange={handleInputChange}
                  error={errors?.lastName}
                  required
                  disabled={isLoading}
                />
              </div>

              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <Input
                  label="Adresse courriel"
                  type="email"
                  name="email"
                  placeholder="jean.tremblay@exemple.ca"
                  value={formData?.email}
                  onChange={handleInputChange}
                  error={errors?.email}
                  // ✅ Option B: on n'affiche plus que c'est pour "vérification"
                  description="Nous utiliserons cette adresse pour votre compte"
                  required
                  disabled={isLoading}
                />
              </div>

              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <Input
                  label="Mot de passe"
                  type="password"
                  name="password"
                  placeholder="••••••••"
                  value={formData?.password}
                  onChange={handleInputChange}
                  error={errors?.password}
                  required
                  disabled={isLoading}
                />
                <PasswordStrengthIndicator password={formData?.password} />
              </div>

              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <Input
                  label="Confirmer le mot de passe"
                  type="password"
                  name="confirmPassword"
                  placeholder="••••••••"
                  value={formData?.confirmPassword}
                  onChange={handleInputChange}
                  error={errors?.confirmPassword}
                  required
                  disabled={isLoading}
                />
              </div>

              <Button
                variant="default"
                size="lg"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={handleContinueToStep2}
                disabled={isLoading}
              >
                Continuer
              </Button>

              <div style={{
                marginTop: 'var(--spacing-lg)',
                paddingTop: 'var(--spacing-md)',
                borderTop: '1px solid var(--color-border)',
                textAlign: 'center'
              }}>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-muted-foreground)'
                }}>
                  Vous avez déjà un compte?{' '}
                  <Link
                    to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
                    state={{ from: redirectTo }}
                    style={{
                      color: 'var(--color-primary)',
                      textDecoration: 'none',
                      fontWeight: 600
                    }}
                  >
                    Se connecter
                  </Link>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ✅ STEP 2 */}
        {currentStep === 2 && (
          <div>
            <div style={{ maxWidth: '1200px', margin: '0 auto var(--spacing-xl)' }}>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-sm)',
                textAlign: 'center'
              }}>
                Choisissez votre plan
              </h2>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--color-muted-foreground)',
                textAlign: 'center',
                marginBottom: 'var(--spacing-xl)',
                lineHeight: 1.6
              }}>
                Commencez avec 3 analyses gratuites ou choisissez un plan payé
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-xl)'
              }} className="md:grid-cols-3">
                {subscriptionTiers?.map((tier) => (
                  <SubscriptionTierCard
                    key={tier?.tier}
                    {...tier}
                    isSelected={formData?.selectedTier === tier?.tier}
                    onSelect={() => handleTierSelect(tier?.tier)}
                  />
                ))}
              </div>
            </div>

            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <div style={{
                background: 'var(--color-card)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-lg)',
                padding: 'var(--spacing-lg) var(--spacing-md)',
                border: '1px solid var(--color-border)'
              }} className="md:p-8">
                <h3 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-md)'
                }}>
                  Conditions d'utilisation
                </h3>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-lg)'
                }}>
                  <Checkbox
                    label="J'accepte les conditions d'utilisation"
                    description="En créant un compte, vous acceptez nos conditions générales d'utilisation"
                    checked={formData?.agreeToTerms}
                    onChange={(e) => handleInputChange({ target: { name: 'agreeToTerms', type: 'checkbox', checked: e?.target?.checked } })}
                    error={errors?.agreeToTerms}
                    required
                    disabled={isLoading}
                  />

                  <Checkbox
                    label="J'accepte la politique de confidentialité"
                    description="Nous respectons votre vie privée et protégeons vos données personnelles"
                    checked={formData?.agreeToPrivacy}
                    onChange={(e) => handleInputChange({ target: { name: 'agreeToPrivacy', type: 'checkbox', checked: e?.target?.checked } })}
                    error={errors?.agreeToPrivacy}
                    required
                    disabled={isLoading}
                  />

                  <Checkbox
                    label="Recevoir les actualités et conseils d'investissement"
                    description="Restez informé des nouvelles fonctionnalités et des meilleures pratiques (optionnel)"
                    checked={formData?.subscribeNewsletter}
                    onChange={(e) => handleInputChange({ target: { name: 'subscribeNewsletter', type: 'checkbox', checked: e?.target?.checked } })}
                    disabled={isLoading}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-sm)'
                }} className="md:flex-row">
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    iconName="ArrowLeft"
                    iconPosition="left"
                    onClick={handleBackToStep1}
                    disabled={isLoading}
                  >
                    Retour
                  </Button>
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="CheckCircle2"
                    iconPosition="right"
                    onClick={handleRegister}
                    loading={isLoading}
                    disabled={isLoading || !formData?.agreeToTerms || !formData?.agreeToPrivacy}
                  >
                    Créer mon compte
                  </Button>
                </div>

                {formData?.selectedTier !== PLAN_SLUGS.TRIAL && (
                  <div style={{
                    marginTop: 'var(--spacing-md)',
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-muted)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--spacing-xs)'
                  }}>
                    <Icon name="Info" size={16} color="var(--color-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-foreground)',
                      lineHeight: 1.5
                    }}>
                      Votre essai gratuit donne droit à 3 analyses au total. Aucun paiement ne sera effectué pour le plan Essai gratuit.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <TrustSignals />
          </div>
        )}
      </div>
    </AuthenticationWrapper>
  );
};

export default Register;
